import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight, LogIn } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import ChatPane from "@/components/ChatPane";
import ChatTrigger from "@/components/ChatTrigger";
import PreviewBanner from "@/components/PreviewBanner";
import RegistrationDeadlineBanner from "@/components/RegistrationDeadlineBanner";
import { useOrganization } from "@/contexts/OrganizationContext";
import { SuperAdminSwitcher } from "@/components/SuperAdminSwitcher";
import UserMenu from "@/components/UserMenu";

interface EventDetails {
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  description: string;
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [whoWeServeOpen, setWhoWeServeOpen] = useState(false);
  const [mobileWhoWeServeOpen, setMobileWhoWeServeOpen] = useState(false);
  const [buildMenuOpen, setBuildMenuOpen] = useState(false);
  const [mobileBuildMenuOpen, setMobileBuildMenuOpen] = useState(false);
  const [eventDetails, setEventDetails] = useState<EventDetails | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { organization, isSuperAdmin } = useOrganization();
  
  // Get feature flags from organization settings
  const enableEvents = organization?.settings?.enable_events ?? true;
  const enableSandbox = organization?.settings?.enable_sandbox ?? true;
  const enableLibrary = organization?.settings?.enable_library ?? true;
  const plannerLabel = organization?.settings?.planner_label || 'Build';

  // Check for preview mode and admin status
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const previewParam = params.get("preview");
    setIsPreviewMode(previewParam === "draft");

    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .in("role", ["admin", "super_admin"])
          .maybeSingle();
        setIsAdmin(!!roles);
      }
    };
    
    if (previewParam === "draft") {
      checkAdmin();
    }
  }, [location.search]);

  useEffect(() => {
    fetchSettings();
  }, [organization?.id]);

  const fetchSettings = async () => {
    try {
      const { data: eventData } = await supabase
        .from("settings")
        .select("key, value")
        .in("key", ["EVENT_NAME", "EVENT_LOCATION", "EVENT_START_DATE", "EVENT_END_DATE", "EVENT_DESCRIPTION"]);

      if (eventData) {
        const details: Record<string, string> = {};
        eventData.forEach(item => details[item.key] = item.value);
        
        setEventDetails({
          name: details.EVENT_NAME || "BuildWithChrist.ai - AI Workshop for Catholic Event Organizers",
          location: details.EVENT_LOCATION || "Virtual Event",
          startDate: details.EVENT_START_DATE || "2025-03-15T09:00:00-05:00",
          endDate: details.EVENT_END_DATE || "2025-03-15T17:00:00-05:00",
          description: details.EVENT_DESCRIPTION || "Learn to use AI tools for Catholic events with hands-on exercises, resource library, and community support.",
        });
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "BuildWithChrist.ai",
    "url": "https://buildwithchrist.ai",
    "logo": "https://buildwithchrist.ai/logo.png",
    "sameAs": ["https://cityonpurpose.com"],
    "description": "A platform empowering Catholic event organizers with AI tools and training"
  };

  const eventSchema = eventDetails ? {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": eventDetails.name,
    "description": eventDetails.description,
    "startDate": eventDetails.startDate,
    "endDate": eventDetails.endDate,
    "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
    "eventStatus": "https://schema.org/EventScheduled",
    "location": { "@type": "VirtualLocation", "url": "https://buildwithchrist.ai" },
    "organizer": { "@type": "Organization", "name": "BuildWithChrist.ai", "url": "https://buildwithchrist.ai" },
    "offers": {
      "@type": "Offer",
      "url": "https://buildwithchrist.ai",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "validFrom": "2025-01-01T00:00:00-05:00"
    }
  } : null;

  const DesktopNavLinks = () => (
    <>
      {organization?.is_flagship && (
        <Link to="/how-it-works" className="hover:text-primary transition-colors font-medium">
          How It Works
        </Link>
      )}
      {organization?.is_flagship && (
        <div 
          className="relative"
          onMouseEnter={() => setWhoWeServeOpen(true)}
          onMouseLeave={() => setWhoWeServeOpen(false)}
        >
          <button className="flex items-center gap-1 hover:text-primary transition-colors font-medium">
            Who We Serve
            <ChevronDown size={16} className={`transition-transform ${whoWeServeOpen ? 'rotate-180' : ''}`} />
          </button>
          {whoWeServeOpen && (
            <div className="absolute top-full left-0 pt-2 z-50">
              <div className="bg-card border border-border rounded-lg shadow-lg py-2 min-w-[160px]">
                <Link 
                  to="/attendees" 
                  className="block px-4 py-2 hover:bg-muted transition-colors"
                  onClick={() => setWhoWeServeOpen(false)}
                >
                  Attendees
                </Link>
                <Link 
                  to="/sponsors" 
                  className="block px-4 py-2 hover:bg-muted transition-colors"
                  onClick={() => setWhoWeServeOpen(false)}
                >
                  Sponsors
                </Link>
                <Link 
                  to="/nonprofits" 
                  className="block px-4 py-2 hover:bg-muted transition-colors"
                  onClick={() => setWhoWeServeOpen(false)}
                >
                  Nonprofits
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
      {enableEvents && (
        <Link to="/events" className="hover:text-primary transition-colors font-medium">
          Events
        </Link>
      )}
      {enableLibrary && (
        <Link to="/library" className="hover:text-primary transition-colors font-medium">
          Learn
        </Link>
      )}
      {enableSandbox && (
        <div 
          className="relative"
          onMouseEnter={() => setBuildMenuOpen(true)}
          onMouseLeave={() => setBuildMenuOpen(false)}
        >
          <button className="flex items-center gap-1 hover:text-primary transition-colors font-medium">
            {plannerLabel}
            <ChevronDown size={16} className={`transition-transform ${buildMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          {buildMenuOpen && (
            <div className="absolute top-full left-0 pt-2 z-50">
              <div className="bg-card border border-border rounded-lg shadow-lg py-2 min-w-[180px]">
                <Link 
                  to="/sandbox" 
                  className="block px-4 py-2 hover:bg-muted transition-colors"
                  onClick={() => setBuildMenuOpen(false)}
                >
                  Sandbox
                </Link>
                <Link 
                  to="/challenges" 
                  className="block px-4 py-2 hover:bg-muted transition-colors"
                  onClick={() => setBuildMenuOpen(false)}
                >
                  Challenge Repository
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  const MobileNavLinks = () => (
    <>
      {organization?.is_flagship && (
        <Link 
          to="/how-it-works" 
          className="block py-3 hover:text-primary transition-colors font-medium"
          onClick={() => setMobileMenuOpen(false)}
        >
          How It Works
        </Link>
      )}
      {organization?.is_flagship && (
        <div>
          <button 
            onClick={() => setMobileWhoWeServeOpen(!mobileWhoWeServeOpen)}
            className="flex items-center gap-1 py-2 hover:text-primary transition-colors font-medium w-full"
          >
            Who We Serve
            <ChevronDown size={16} className={`transition-transform ${mobileWhoWeServeOpen ? 'rotate-180' : ''}`} />
          </button>
          {mobileWhoWeServeOpen && (
            <div className="pl-4 space-y-1">
              <Link 
                to="/attendees" 
                className="block py-2 text-muted-foreground hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Attendees
              </Link>
              <Link 
                to="/sponsors" 
                className="block py-2 text-muted-foreground hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sponsors
              </Link>
              <Link 
                to="/nonprofits" 
                className="block py-2 text-muted-foreground hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Nonprofits
              </Link>
            </div>
          )}
        </div>
      )}
      {enableEvents && (
        <Link 
          to="/events" 
          className="block py-3 hover:text-primary transition-colors font-medium"
          onClick={() => setMobileMenuOpen(false)}
        >
          Events
        </Link>
      )}
      {enableLibrary && (
        <Link 
          to="/library" 
          className="block py-3 hover:text-primary transition-colors font-medium"
          onClick={() => setMobileMenuOpen(false)}
        >
          Learn
        </Link>
      )}
      {enableSandbox && (
        <div>
          <button 
            onClick={() => setMobileBuildMenuOpen(!mobileBuildMenuOpen)}
            className="flex items-center gap-1 py-2 hover:text-primary transition-colors font-medium w-full"
          >
            {plannerLabel}
            <ChevronDown size={16} className={`transition-transform ${mobileBuildMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          {mobileBuildMenuOpen && (
            <div className="pl-4 space-y-1">
              <Link 
                to="/sandbox" 
                className="block py-2 text-muted-foreground hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sandbox
              </Link>
              <Link 
                to="/challenges" 
                className="block py-2 text-muted-foreground hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Challenge Repository
              </Link>
            </div>
          )}
        </div>
      )}
    </>
  );

  // Cross icon component for logo - scales with text using em units
  const CrossIcon = ({ className = "" }: { className?: string }) => (
    <svg viewBox="0 0 10 16" className={`inline-block -ml-0.5 w-[0.6em] h-[1em] ${className}`} style={{ marginBottom: '0.1em' }}>
      <rect x="3" y="0" width="4" height="16" fill="currentColor" rx="1"/>
      <rect x="0" y="4" width="10" height="3" fill="currentColor" rx="1"/>
    </svg>
  );

  return (
    <SidebarProvider defaultOpen={false}>
      <ChatPane />
      <SidebarInset className="min-h-screen flex flex-col w-full">
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
          {eventSchema && <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>}
        </Helmet>

        <RegistrationDeadlineBanner />
        {isSuperAdmin && <SuperAdminSwitcher />}
      
        <nav className="sticky top-0 z-50 bg-background/90 backdrop-blur border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Logo - scales proportionally */}
              <div className="flex items-center flex-shrink-0">
                <Link to="/" className="text-base lg:text-xl font-bold tracking-tight font-display whitespace-nowrap">
                  {organization?.slug === 'spes' ? (
                    <span className="text-primary font-bold">SPES</span>
                  ) : organization?.is_flagship ? (
                    <>BuildWithChrist<span className="text-primary">.a<CrossIcon /></span></>
                  ) : (
                    <span>{organization?.settings?.site_title || organization?.name || 'Home'}</span>
                  )}
                </Link>
              </div>

              {/* Desktop: Full navigation (lg and up) */}
              <div className="hidden lg:flex items-center space-x-6 text-sm">
                <DesktopNavLinks />
                <div className="flex items-center gap-3">
                  {organization?.is_flagship && (
                    <Link
                      to="/attendees#register-form"
                      className="bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-2 rounded-full font-semibold transition-colors"
                    >
                      Register Now
                    </Link>
                  )}
                  <UserMenu />
                </div>
              </div>

              {/* Tablet: Register Now + Hamburger (md to lg) */}
              <div className="hidden md:flex lg:hidden items-center gap-3">
                {organization?.is_flagship && (
                  <Link
                    to="/attendees#register-form"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold transition-colors"
                  >
                    Register Now
                  </Link>
                )}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-muted-foreground hover:text-foreground p-2"
                >
                  {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>

              {/* Mobile: Hamburger only (below md) */}
              <div className="md:hidden flex items-center">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-muted-foreground hover:text-foreground p-2"
                >
                  {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile/Tablet menu dropdown */}
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-border">
              <div className="px-4 pt-2 pb-3 flex flex-col">
                <MobileNavLinks />
                {/* Only show Register Now in mobile menu on small screens (it's already visible on tablet) */}
                <div className="md:hidden">
                  {organization?.is_flagship && (
                    <Link
                      to="/attendees#register-form"
                      className="block w-full text-center bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-2 rounded-lg font-semibold transition-colors mt-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Register Now
                    </Link>
                  )}
                </div>
                <UserMenu isMobile onMobileMenuClose={() => setMobileMenuOpen(false)} />
              </div>
            </div>
          )}
        </nav>

        {isPreviewMode && isAdmin && <PreviewBanner />}

        <main className="flex-grow">{children}</main>

        {/* Redesigned Footer */}
        <footer className="border-t border-border mt-auto bg-card/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Brand */}
              <div className="md:col-span-1">
                <Link to="/" className="text-lg font-bold tracking-tight font-display">
                  {organization?.is_flagship ? (
                    <>BuildWithChrist<span className="text-primary">.a<CrossIcon /></span></>
                  ) : (
                    <span>{organization?.settings?.site_title || organization?.name || 'Home'}</span>
                  )}
                </Link>
                <p className="text-sm text-muted-foreground mt-2">
                  {organization?.settings?.site_description || 'Building AI tools for nonprofits serving those most in need.'}
                </p>
                {/* Join the Next Build - Ghost Button - Only for flagship */}
                {organization?.is_flagship && (
                  <Link 
                    to="/attendees#register-form"
                    className="inline-flex items-center gap-1.5 mt-4 text-sm text-muted-foreground hover:text-foreground px-3 py-1.5 -ml-3 rounded-lg hover:bg-primary/10 transition-all group"
                  >
                    Join the Next Build
                    <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                )}
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-semibold mb-3 text-sm">Quick Links</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {organization?.is_flagship && (
                    <li><Link to="/how-it-works" className="hover:text-foreground transition-colors">How It Works</Link></li>
                  )}
                  {enableEvents && (
                    <li><Link to="/events" className="hover:text-foreground transition-colors">Events</Link></li>
                  )}
                  {enableLibrary && (
                    <li>
                      <Link to="/library" className="hover:text-foreground transition-colors">
                        {organization?.slug === 'spes' ? 'Resource Repository' : 'Resource Library'}
                      </Link>
                    </li>
                  )}
                  {enableSandbox && (
                    <li><Link to="/sandbox" className="hover:text-foreground transition-colors">{plannerLabel}</Link></li>
                  )}
                </ul>
              </div>

              {/* Get Involved - Only show for flagship */}
              {organization?.is_flagship && (
                <div>
                  <h4 className="font-semibold mb-3 text-sm">Get Involved</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li><Link to="/attendees" className="hover:text-foreground transition-colors">Attend an Event</Link></li>
                    <li><Link to="/sponsors" className="hover:text-foreground transition-colors">Become a Sponsor</Link></li>
                    <li><Link to="/nonprofits" className="hover:text-foreground transition-colors">Partner With Us</Link></li>
                  </ul>
                </div>
              )}

              {/* More */}
              <div>
                <h4 className="font-semibold mb-3 text-sm">More</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {organization?.is_flagship && (
                    <li>
                      <a 
                        href="https://cityonpurpose.com?utm_source=buildwithchrist&utm_medium=website&utm_campaign=bwc_homepage&utm_content=footer_nav" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="hover:text-foreground transition-colors"
                      >
                        City on Purpose
                      </a>
                    </li>
                  )}
                  <li><Link to="/about" className="hover:text-foreground transition-colors">Contact Us</Link></li>
                  {organization?.is_flagship && (
                    <li><Link to="/feature-requests" className="hover:text-foreground transition-colors">Feature Requests</Link></li>
                  )}
                  <li><Link to="/admin" className="hover:text-foreground transition-colors flex items-center gap-1.5">
                    <LogIn size={14} /> Admin Login
                  </Link></li>
                </ul>
              </div>
            </div>

            <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
              {organization?.is_flagship ? (
                <>
                  © {new Date().getFullYear()} BuildWithChrist.ai. A project by{" "}
                  <a 
                    href="https://cityonpurpose.com?utm_source=buildwithchrist&utm_medium=website&utm_campaign=bwc_homepage&utm_content=footer_copyright" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-primary underline hover:text-primary/80"
                  >
                    City on Purpose
                  </a>
                </>
              ) : (
                <>© {new Date().getFullYear()} {organization?.settings?.site_title || organization?.name}</>
              )}
            </div>
          </div>
        </footer>
        <ChatTrigger />
      </SidebarInset>
    </SidebarProvider>
  );
};

export default Layout;
